import {
  Box,
  Button,
  AppBar,
  Container,
  Toolbar,
  Typography,
  Drawer,
  Divider,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import MenuItem from '@mui/material/MenuItem';
import { AppConfig } from '../../core/constants';
import { useAuth } from '../auth';
import { scrollToSection } from '../../utils';

export function Header() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['common', 'header']);
  const { isAuthenticated } = useAuth();

  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpenMenu(newOpen);
  };

  return (
    <AppBar
      sx={{
        bgcolor: theme.palette.primary.contrastText,
        position: 'fixed',
        width: '100%',
        zIndex: 9999,
        minHeight: '64px',
        boxShadow: 'none',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            display: 'flex',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              noWrap
              component="a"
              href="/"
              sx={{
                fontWeight: 700,
                letterSpacing: '.3rem',
                color: theme.palette.primary.main,
                textDecoration: 'none',
              }}
            >
              {AppConfig.appName}
            </Typography>
          </Box>
          <Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 4 }}>
              <Button
                variant="text"
                size="large"
                onClick={() => navigate('/hub')}
                sx={{
                  fontWeight: theme.typography.h3.fontWeight,
                  color() {
                    return theme.palette.text.primary;
                  },
                }}
              >
                {t('header:hub')}
              </Button>
              {location.pathname === '/' && (
                <Button
                  variant="text"
                  size="large"
                  onClick={() => scrollToSection('trust')}
                  sx={{
                    fontWeight: theme.typography.h3.fontWeight,
                    color() {
                      return theme.palette.text.primary;
                    },
                  }}
                >
                  {t('header:trust')}
                </Button>
              )}
              <Button
                variant="text"
                size="large"
                onClick={() => navigate('/pricing')}
                sx={{
                  fontWeight: theme.typography.h3.fontWeight,
                  color() {
                    return theme.palette.text.primary;
                  },
                }}
              >
                {t('header:pricing')}
              </Button>
              <Button
                variant="text"
                size="large"
                onClick={() => navigate('/about')}
                sx={{
                  fontWeight: theme.typography.h3.fontWeight,
                  color() {
                    return theme.palette.text.primary;
                  },
                }}
              >
                {t('header:aboutUs')}
              </Button>
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 1,
                alignItems: 'center',
              }}
            >
              {!isAuthenticated ? (
                <>
                  <Button
                    variant="text"
                    color="primary"
                    size="medium"
                    onClick={() => navigate('/auth/login')}
                  >
                    {t('common:login')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                    onClick={() => navigate('/auth/signup')}
                  >
                    {t('common:signUp')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => navigate('/app')}
                  startIcon={<DashboardCustomizeIcon />}
                >
                  {t('common:GoToDashboard')}
                </Button>
              )}
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <Button
                variant="text"
                color="primary"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ minWidth: '30px', p: '4px' }}
              >
                <MenuIcon />
              </Button>
              <Drawer
                anchor="right"
                open={openMenu}
                onClose={toggleDrawer(false)}
              >
                <Box
                  sx={{
                    minWidth: '60dvw',
                    p: 2,
                    backgroundColor: 'background.paper',
                    flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'end',
                      flexGrow: 1,
                    }}
                  ></Box>
                  <MenuItem
                    onClick={() => {
                      navigate('/');
                      setOpenMenu(false);
                    }}
                  >
                    {t('header:home')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/hub');
                      setOpenMenu(false);
                    }}
                  >
                    {t('header:hub')}
                  </MenuItem>
                  {location.pathname === '/' && (
                    <MenuItem
                      onClick={() => {
                        scrollToSection('trust');
                        setOpenMenu(false);
                      }}
                    >
                      {t('header:trust')}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      navigate('/pricing');
                      setOpenMenu(false);
                    }}
                  >
                    {t('header:pricing')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate('/about');
                      setOpenMenu(false);
                    }}
                  >
                    {t('header:aboutUs')}
                  </MenuItem>
                  <Divider />
                  {!isAuthenticated ? (
                    <>
                      <MenuItem>
                        <Button
                          color="primary"
                          variant="outlined"
                          sx={{ width: '100%' }}
                          onClick={() => navigate('/auth/login')}
                        >
                          {t('common:login')}
                        </Button>
                      </MenuItem>
                      <MenuItem>
                        <Button
                          color="primary"
                          variant="contained"
                          sx={{ width: '100%' }}
                          onClick={() => navigate('/auth/signup')}
                        >
                          {t('common:signUp')}
                        </Button>
                      </MenuItem>
                    </>
                  ) : (
                    <MenuItem>
                      <Button
                        color="primary"
                        variant="text"
                        sx={{ width: '100%' }}
                        onClick={() => navigate('/app')}
                        startIcon={<DashboardCustomizeIcon />}
                      >
                        {t('common:GoToDashboard')}
                      </Button>
                    </MenuItem>
                  )}
                </Box>
              </Drawer>
            </Box>
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}
